import React, { useState, useEffect } from 'react';
import { 
  View, 
  Text, 
  StyleSheet, 
  Pressable, 
  ScrollView,
  Switch,
  Platform,
  Dimensions,
  ActivityIndicator,
  Alert,
  TextInput,
} from 'react-native';
import { theme as baseTheme } from '../styles/theme';
import { PRICING, FREE_TIER } from '../config/stripe';
import { useAuth } from '../hooks/useAuth';
import { db } from '../config/firebase';
import { doc, getDoc, setDoc, collection, onSnapshot, updateDoc, query, orderBy } from 'firebase/firestore';

const { width: SCREEN_WIDTH } = Dimensions.get('window');
const isWeb = Platform.OS === 'web';

const getBreakpoint = (w) => {
  if (w < 480) return 'mobile';
  if (w < 768) return 'mobileLarge';
  return 'desktop';
};

const PLAN_CYCLE = ['free', 'pro', 'lifetime'];

const PlanBadge = ({ tier, theme }) => {
  const colors = { free: theme.backgroundAlt, pro: theme.primary, lifetime: '#F59E0B' };
  const textColors = { free: theme.textSecondary, pro: '#fff', lifetime: '#fff' };
  const labels = { free: 'FREE', pro: 'PRO', lifetime: 'LTD' };
  return (
    <View style={[styles.planBadge, { backgroundColor: colors[tier] || colors.free }]}>
      <Text style={[styles.planBadgeText, { color: textColors[tier] || textColors.free }]}>
        {labels[tier] || 'FREE'}
      </Text>
    </View>
  );
};

// ---- Plan Editor Card ----
const PlanEditor = ({ label, accentColor, plan, onChange, theme }) => {
  const [featuresText, setFeaturesText] = useState((plan.features || []).join('\n'));

  useEffect(() => {
    setFeaturesText((plan.features || []).join('\n'));
  }, []);

  const applyFeatures = (text) => {
    setFeaturesText(text);
    onChange({ features: text.split('\n').map(f => f.trim()).filter(Boolean) });
  };

  return (
    <View style={[styles.section, { backgroundColor: theme.surface }]}>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <View style={{ width: 10, height: 10, borderRadius: 5, backgroundColor: accentColor }} />
        <Text style={[styles.sectionTitle, { color: theme.textPrimary, marginBottom: 0 }]}>{label}</Text>
      </View>
      <Text style={[styles.sectionDescription, { color: theme.textMuted }]}>Edit the plan details shown on the Plans page.</Text>

      <Text style={[styles.inputLabel, { color: theme.textSecondary }]}>Name</Text>
      <TextInput
        style={[styles.input, { backgroundColor: theme.backgroundAlt, borderColor: theme.border, color: theme.textPrimary }]}
        value={plan.name || ''}
        onChangeText={v => onChange({ name: v })}
        placeholder="Plan name"
        placeholderTextColor={theme.textMuted}
      />

      <Text style={[styles.inputLabel, { color: theme.textSecondary }]}>Price (USD)</Text>
      <TextInput
        style={[styles.input, { backgroundColor: theme.backgroundAlt, borderColor: theme.border, color: theme.textPrimary }]}
        value={String(plan.price ?? '')}
        onChangeText={v => onChange({ price: parseFloat(v) || 0 })}
        keyboardType="decimal-pad"
        placeholder="14.99"
        placeholderTextColor={theme.textMuted}
      />

      <Text style={[styles.inputLabel, { color: theme.textSecondary }]}>Stripe Price ID</Text>
      <TextInput
        style={[styles.input, { backgroundColor: theme.backgroundAlt, borderColor: theme.border, color: theme.textPrimary }]}
        value={plan.id || ''}
        onChangeText={v => onChange({ id: v })}
        placeholder="price_xxx"
        placeholderTextColor={theme.textMuted}
        autoCapitalize="none"
        autoCorrect={false}
      />

      <Text style={[styles.inputLabel, { color: theme.textSecondary }]}>Share Credits / Month (0 = unlimited for LTD)</Text>
      <TextInput
        style={[styles.input, { backgroundColor: theme.backgroundAlt, borderColor: theme.border, color: theme.textPrimary }]}
        value={String(plan.shareCreditsPerMonth ?? '')}
        onChangeText={v => onChange({ shareCreditsPerMonth: v === 'Infinity' ? Infinity : parseInt(v) || 0 })}
        keyboardType="number-pad"
        placeholder="10"
        placeholderTextColor={theme.textMuted}
      />

      <Text style={[styles.inputLabel, { color: theme.textSecondary }]}>Features (one per line)</Text>
      <TextInput
        style={[styles.input, styles.inputMultiline, { backgroundColor: theme.backgroundAlt, borderColor: theme.border, color: theme.textPrimary }]}
        value={featuresText}
        onChangeText={applyFeatures}
        multiline
        numberOfLines={5}
        placeholder="Unlimited sheets&#10;Priority support"
        placeholderTextColor={theme.textMuted}
      />
    </View>
  );
};

export const AdminScreen = ({ navigation }) => {
  const { user } = useAuth();
  const [screenWidth, setScreenWidth] = useState(SCREEN_WIDTH);
  const [activeTab, setActiveTab] = useState('stats');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    pricingMode: 'LTD',
    stripeMode: 'sandbox',
    ltdActive: true,
    ltdEndDate: null,
  });
  // Subscriptions tab state
  const [planDraft, setPlanDraft] = useState(null); // null = loading
  const [planSaving, setPlanSaving] = useState(false);
  const [planSaveMsg, setPlanSaveMsg] = useState('');
  const [stats, setStats] = useState({
    totalUsers: 0,
    proUsers: 0,
    totalSheets: 0,
    revenue: 0,
  });

  // Users tab state
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [updatingUser, setUpdatingUser] = useState(null);

  const theme = baseTheme.light;
  const breakpoint = getBreakpoint(screenWidth);
  const isMobile = breakpoint === 'mobile' || breakpoint === 'mobileLarge';

  useEffect(() => {
    if (!isWeb) return;
    const handleResize = () => setScreenWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (isWeb && typeof document !== 'undefined') {
    document.title = 'Admin - SheetShed';
  }

  // Guard: admin only
  useEffect(() => {
    if (user && user.userType !== 'admin') {
      if (isWeb) {
        alert('Access denied. Admin only.');
      }
      navigation.goBack();
    }
  }, [user]);

  // Fetch app settings + stats + plans
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const [settingsDoc, statsDoc, plansDoc] = await Promise.all([
          getDoc(doc(db, 'settings', 'app')),
          getDoc(doc(db, 'settings', 'stats')),
          getDoc(doc(db, 'settings', 'plans')),
        ]);
        if (settingsDoc.exists()) setSettings(prev => ({ ...prev, ...settingsDoc.data() }));
        if (statsDoc.exists()) setStats(prev => ({ ...prev, ...statsDoc.data() }));
        // Initialise planDraft from Firestore or fall back to hardcoded PRICING
        const plansData = plansDoc.exists() ? plansDoc.data() : null;
        setPlanDraft({
          LTD: {
            name: PRICING.LTD.name,
            price: PRICING.LTD.price,
            features: PRICING.LTD.features,
            shareCreditsPerMonth: PRICING.LTD.shareCreditsPerMonth,
            maxSheets: PRICING.LTD.maxSheets,
            id: PRICING.LTD.id,
            ...(plansData?.LTD || {}),
          },
          MONTHLY: {
            name: PRICING.MONTHLY.name,
            price: PRICING.MONTHLY.price,
            features: PRICING.MONTHLY.features,
            shareCreditsPerMonth: PRICING.MONTHLY.shareCreditsPerMonth,
            maxSheets: PRICING.MONTHLY.maxSheets,
            id: PRICING.MONTHLY.id,
            ...(plansData?.MONTHLY || {}),
          },
        });
      } catch (err) {
        console.error('Error fetching settings:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  // Real-time users listener (loaded when Users tab is opened)
  useEffect(() => {
    if (activeTab !== 'users') return;
    setUsersLoading(true);
    const q = query(collection(db, 'users'), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(q, (snap) => {
      setUsers(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setUsersLoading(false);
    }, (err) => {
      console.error('Users snapshot error:', err);
      setUsersLoading(false);
    });
    return unsub;
  }, [activeTab]);

  const saveSettings = async (newSettings) => {
    setSaving(true);
    try {
      await setDoc(doc(db, 'settings', 'app'), {
        ...newSettings,
        updatedAt: new Date().toISOString(),
        updatedBy: user?.uid,
      }, { merge: true });
      setSettings(newSettings);
    } catch (err) {
      console.error('Error saving settings:', err);
    } finally {
      setSaving(false);
    }
  };

  const togglePricingMode = () => {
    const newMode = settings.pricingMode === 'LTD' ? 'SUBSCRIPTION' : 'LTD';
    saveSettings({ ...settings, pricingMode: newMode });
  };

  const toggleLTDActive = () => {
    saveSettings({ ...settings, ltdActive: !settings.ltdActive });
  };

  // Cycle through plan tiers for a user
  const handleCyclePlan = async (u) => {
    const current = u.subscription?.tier || 'free';
    const next = PLAN_CYCLE[(PLAN_CYCLE.indexOf(current) + 1) % PLAN_CYCLE.length];
    setUpdatingUser(u.id);
    try {
      await updateDoc(doc(db, 'users', u.id), {
        subscription: { tier: next, status: 'active', updatedAt: new Date().toISOString() },
      });
    } catch (err) {
      console.error('Plan update error:', err);
    } finally {
      setUpdatingUser(null);
    }
  };

  // Toggle ban
  const handleToggleBan = async (u) => {
    const nowBanned = !u.banned;
    const confirmMsg = nowBanned
      ? `Ban ${u.email}? They will be signed out and blocked.`
      : `Unban ${u.email}?`;
    if (!isWeb || window.confirm(confirmMsg)) {
      setUpdatingUser(u.id);
      try {
        await updateDoc(doc(db, 'users', u.id), { banned: nowBanned });
      } catch (err) {
        console.error('Ban toggle error:', err);
      } finally {
        setUpdatingUser(null);
      }
    }
  };

  const filteredUsers = users.filter(u => {
    if (!userSearch.trim()) return true;
    const q = userSearch.toLowerCase();
    return u.email?.toLowerCase().includes(q) || u.displayName?.toLowerCase().includes(q);
  });

  if (loading) {
    return (
      <View style={[styles.container, styles.centered, { backgroundColor: theme.background }]}>
        <ActivityIndicator size="large" color={theme.primary} />
        <Text style={[styles.loadingText, { color: theme.textSecondary }]}>Loading admin panel...</Text>
      </View>
    );
  }

  return (
    <View style={[styles.container, { backgroundColor: theme.background }]}>
      {/* Header */}
      <View style={[styles.header, { backgroundColor: theme.surface, borderBottomColor: theme.border }]}>
        <Pressable style={styles.backButton} onPress={() => navigation.goBack()}>
          <Text style={[styles.backArrow, { color: theme.textSecondary }]}>←</Text>
          <Text style={[styles.backText, { color: theme.textSecondary }]}>Back</Text>
        </Pressable>
        <Text style={[styles.headerTitle, { color: theme.textPrimary }]}>Admin Dashboard</Text>
        <View style={[styles.adminBadge, { backgroundColor: theme.primary }]}>
          <Text style={styles.adminBadgeText}>Admin</Text>
        </View>
      </View>

      {/* Tabs */}
      <View style={[styles.tabs, { backgroundColor: theme.surface, borderBottomColor: theme.border }]}>
        {[
          { id: 'stats', label: 'Stats & Settings' },
          { id: 'users', label: `Users${users.length ? ` (${users.length})` : ''}` },
          { id: 'subscriptions', label: 'Subscriptions' },
        ].map(tab => (
          <Pressable
            key={tab.id}
            style={[styles.tab, activeTab === tab.id && { borderBottomColor: theme.primary }]}
            onPress={() => setActiveTab(tab.id)}
          >
            <Text style={[styles.tabText, { color: activeTab === tab.id ? theme.primary : theme.textSecondary }]}>
              {tab.label}
            </Text>
          </Pressable>
        ))}
      </View>

      {/* Stats Tab */}
      {activeTab === 'stats' && (
        <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollContent} showsVerticalScrollIndicator={false}>
          {/* Stat cards */}
          <View style={styles.statsGrid}>
            {[
              { value: stats.totalUsers, label: 'Total Users', color: theme.textPrimary },
              { value: stats.proUsers, label: 'Pro Users', color: theme.primary },
              { value: stats.totalSheets, label: 'Sheets Uploaded', color: theme.textPrimary },
              { value: `$${stats.revenue || 0}`, label: 'Total Revenue', color: '#22C55E' },
            ].map((s, i) => (
              <View key={i} style={[styles.statCard, { backgroundColor: theme.surface }]}>
                <Text style={[styles.statValue, { color: s.color }]}>{s.value}</Text>
                <Text style={[styles.statLabel, { color: theme.textSecondary }]}>{s.label}</Text>
              </View>
            ))}
          </View>

          {/* Pricing mode toggle */}
          <View style={[styles.section, { backgroundColor: theme.surface }]}>
            <Text style={[styles.sectionTitle, { color: theme.textPrimary }]}>Pricing Mode</Text>
            <Text style={[styles.sectionDescription, { color: theme.textSecondary }]}>
              Switch between Lifetime Deal (one-time) and Subscription (recurring) pricing models.
            </Text>
            <View style={styles.toggleContainer}>
              <View style={styles.toggleOption}>
                <Text style={[styles.toggleLabel, { color: settings.pricingMode === 'LTD' ? theme.primary : theme.textMuted }]}>Lifetime Deal</Text>
                <Text style={[styles.togglePrice, { color: theme.textSecondary }]}>${PRICING.LTD.price} one-time</Text>
              </View>
              <Switch
                value={settings.pricingMode === 'SUBSCRIPTION'}
                onValueChange={togglePricingMode}
                trackColor={{ false: theme.primary, true: theme.secondary }}
                thumbColor={theme.surface}
                disabled={saving}
              />
              <View style={[styles.toggleOption, { alignItems: 'flex-end' }]}>
                <Text style={[styles.toggleLabel, { color: settings.pricingMode === 'SUBSCRIPTION' ? theme.primary : theme.textMuted }]}>Subscription</Text>
                <Text style={[styles.togglePrice, { color: theme.textSecondary }]}>${PRICING.MONTHLY.price}/mo</Text>
              </View>
            </View>
            <View style={[styles.currentModeIndicator, { backgroundColor: theme.primary }]}>
              <Text style={styles.currentModeText}>
                Active: {settings.pricingMode === 'LTD' ? 'Lifetime Deal' : 'Subscription'}
              </Text>
            </View>
          </View>

          {/* LTD controls */}
          {settings.pricingMode === 'LTD' && (
            <View style={[styles.section, { backgroundColor: theme.surface }]}>
              <Text style={[styles.sectionTitle, { color: theme.textPrimary }]}>Lifetime Deal Settings</Text>
              <View style={styles.settingRow}>
                <View style={{ flex: 1 }}>
                  <Text style={[styles.settingLabel, { color: theme.textPrimary }]}>LTD Active</Text>
                  <Text style={[styles.settingDescription, { color: theme.textSecondary }]}>When off, users see "Coming Soon" on the plans page</Text>
                </View>
                <Switch
                  value={settings.ltdActive}
                  onValueChange={toggleLTDActive}
                  trackColor={{ false: theme.border, true: '#22C55E' }}
                  thumbColor={theme.surface}
                  disabled={saving}
                />
              </View>
            </View>
          )}

          {/* Quick actions */}
          <View style={[styles.section, { backgroundColor: theme.surface }]}>
            <Text style={[styles.sectionTitle, { color: theme.textPrimary }]}>Quick Actions</Text>
            <View style={{ flexDirection: 'row', gap: 12, flexWrap: 'wrap' }}>
              <Pressable style={[styles.actionButton, { backgroundColor: theme.backgroundAlt }]} onPress={() => navigation.navigate('Plans')}>
                <Text style={[styles.actionLabel, { color: theme.textPrimary }]}>Preview Plans Page</Text>
              </Pressable>
              {isWeb && (
                <Pressable style={[styles.actionButton, { backgroundColor: theme.backgroundAlt }]} onPress={() => window.open('https://dashboard.stripe.com/test/payments', '_blank')}>
                  <Text style={[styles.actionLabel, { color: theme.textPrimary }]}>Stripe Dashboard</Text>
                </Pressable>
              )}
            </View>
          </View>

          {/* Pricing info */}
          <View style={[styles.infoSection, { backgroundColor: theme.backgroundAlt }]}>
            <Text style={[styles.sectionTitle, { color: theme.textPrimary }]}>Pricing Strategy</Text>
            <Text style={[styles.infoText, { color: theme.textSecondary }]}>
              <Text style={{ fontWeight: '700' }}>Phase 1 (LTD): </Text>${PRICING.LTD.price} one-time "Founding Edition" for early adopters.
            </Text>
            <Text style={[styles.infoText, { color: theme.textSecondary }]}>
              <Text style={{ fontWeight: '700' }}>Phase 2 (Subscription): </Text>${PRICING.MONTHLY.price}/month — unlimited uploads + 10 rolling share credits.
            </Text>
            <Text style={[styles.infoText, { color: theme.textSecondary }]}>
              <Text style={{ fontWeight: '700' }}>The ONE paywall: </Text>Unlimited sheet storage — everything else stays free.
            </Text>
          </View>
        </ScrollView>
      )}

      {/* Users Tab */}
      {activeTab === 'users' && (
        <View style={{ flex: 1 }}>
          {/* Search bar */}
          <View style={[styles.userSearchRow, { backgroundColor: theme.surface, borderBottomColor: theme.border }]}>
            <TextInput
              style={[styles.userSearchInput, { backgroundColor: theme.backgroundAlt, borderColor: theme.border, color: theme.textPrimary }]}
              value={userSearch}
              onChangeText={setUserSearch}
              placeholder="Search by name or email..."
              placeholderTextColor={theme.textMuted}
            />
            <Text style={[styles.userCount, { color: theme.textMuted }]}>{filteredUsers.length} user{filteredUsers.length !== 1 ? 's' : ''}</Text>
          </View>

          {usersLoading ? (
            <View style={styles.centered}>
              <ActivityIndicator size="large" color={theme.primary} />
            </View>
          ) : (
            <ScrollView style={styles.scrollView} contentContainerStyle={{ paddingBottom: 40 }} showsVerticalScrollIndicator={false}>
              {filteredUsers.map(u => {
                const tier = u.subscription?.tier || 'free';
                const isUpdating = updatingUser === u.id;
                return (
                  <View key={u.id} style={[styles.userRow, { backgroundColor: theme.surface, borderBottomColor: theme.border, opacity: u.banned ? 0.6 : 1 }]}>
                    {/* Avatar */}
                    <View style={[styles.userAvatar, { backgroundColor: u.banned ? theme.error : theme.primary }]}>
                      <Text style={styles.userAvatarText}>
                        {(u.displayName || u.email || '?').charAt(0).toUpperCase()}
                      </Text>
                    </View>

                    {/* Info */}
                    <View style={{ flex: 1, minWidth: 0 }}>
                      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <Text style={[styles.userName, { color: theme.textPrimary }]} numberOfLines={1}>
                          {u.displayName || '(no name)'}
                        </Text>
                        <PlanBadge tier={tier} theme={theme} />
                        {u.banned && (
                          <View style={[styles.planBadge, { backgroundColor: theme.error }]}>
                            <Text style={[styles.planBadgeText, { color: '#fff' }]}>BANNED</Text>
                          </View>
                        )}
                        {u.userType === 'admin' && (
                          <View style={[styles.planBadge, { backgroundColor: '#8B5CF6' }]}>
                            <Text style={[styles.planBadgeText, { color: '#fff' }]}>ADMIN</Text>
                          </View>
                        )}
                      </View>
                      <Text style={[styles.userEmail, { color: theme.textMuted }]} numberOfLines={1}>{u.email}</Text>
                      <Text style={[styles.userMeta, { color: theme.textMuted }]}>
                        {u.sheetCount || 0} sheets · joined {u.createdAt ? new Date(u.createdAt).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : '?'}
                      </Text>
                    </View>

                    {/* Actions */}
                    <View style={styles.userActions}>
                      {isUpdating ? (
                        <ActivityIndicator size="small" color={theme.primary} />
                      ) : (
                        <>
                          {/* Cycle plan */}
                          <Pressable
                            style={[styles.userActionBtn, { backgroundColor: theme.backgroundAlt, borderColor: theme.border }]}
                            onPress={() => handleCyclePlan(u)}
                          >
                            <Text style={[styles.userActionText, { color: theme.textPrimary }]}>
                              → {PLAN_CYCLE[(PLAN_CYCLE.indexOf(tier) + 1) % PLAN_CYCLE.length]}
                            </Text>
                          </Pressable>

                          {/* Ban toggle (don't allow banning yourself) */}
                          {u.id !== user?.uid && (
                            <Pressable
                              style={[styles.userActionBtn, { backgroundColor: u.banned ? '#22C55E18' : theme.error + '18', borderColor: u.banned ? '#22C55E' : theme.error }]}
                              onPress={() => handleToggleBan(u)}
                            >
                              <Text style={[styles.userActionText, { color: u.banned ? '#22C55E' : theme.error }]}>
                                {u.banned ? 'Unban' : 'Ban'}
                              </Text>
                            </Pressable>
                          )}
                        </>
                      )}
                    </View>
                  </View>
                );
              })}
              {filteredUsers.length === 0 && (
                <View style={styles.centered}>
                  <Text style={[styles.emptyText, { color: theme.textMuted }]}>
                    {userSearch ? 'No users match your search' : 'No users found'}
                  </Text>
                </View>
              )}
            </ScrollView>
          )}
        </View>
      )}

      {/* Subscriptions Tab */}
      {activeTab === 'subscriptions' && (
        <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollContent} showsVerticalScrollIndicator={false}>
          {!planDraft ? (
            <View style={styles.centered}><ActivityIndicator color={theme.primary} /></View>
          ) : (
            <>
              {/* Master toggles */}
              <View style={[styles.section, { backgroundColor: theme.surface }]}>
                <Text style={[styles.sectionTitle, { color: theme.textPrimary }]}>Pricing Mode</Text>
                <Text style={[styles.sectionDescription, { color: theme.textMuted }]}>
                  Controls which plan card is shown on the Plans page.
                </Text>

                {/* LTD vs Subscription toggle */}
                <View style={styles.toggleContainer}>
                  <View style={styles.toggleOption}>
                    <Text style={[styles.toggleLabel, { color: theme.textPrimary }]}>Lifetime Deal (LTD)</Text>
                    <Text style={[styles.togglePrice, { color: theme.textMuted }]}>One-time $14.99</Text>
                  </View>
                  <Switch
                    value={settings.pricingMode === 'SUBSCRIPTION'}
                    onValueChange={async (v) => {
                      const next = v ? 'SUBSCRIPTION' : 'LTD';
                      setSettings(s => ({ ...s, pricingMode: next }));
                      await updateDoc(doc(db, 'settings', 'app'), { pricingMode: next });
                    }}
                    trackColor={{ false: theme.primary, true: '#8B5CF6' }}
                    thumbColor="#fff"
                  />
                  <View style={[styles.toggleOption, { alignItems: 'flex-end' }]}>
                    <Text style={[styles.toggleLabel, { color: theme.textPrimary }]}>Subscription</Text>
                    <Text style={[styles.togglePrice, { color: theme.textMuted }]}>$4.99/month</Text>
                  </View>
                </View>

                <View style={[styles.currentModeIndicator, { backgroundColor: settings.pricingMode === 'LTD' ? theme.primary : '#8B5CF6' }]}>
                  <Text style={styles.currentModeText}>
                    Active mode: {settings.pricingMode === 'LTD' ? 'Lifetime Deal' : 'Monthly Subscription'}
                  </Text>
                </View>
              </View>

              {/* Stripe mode */}
              <View style={[styles.section, { backgroundColor: theme.surface }]}>
                <Text style={[styles.sectionTitle, { color: theme.textPrimary }]}>Stripe Mode</Text>
                <Text style={[styles.sectionDescription, { color: theme.textMuted }]}>
                  Sandbox uses test keys; live uses production keys. Only switch to live in production.
                </Text>
                <View style={styles.settingRow}>
                  <View>
                    <Text style={[styles.settingLabel, { color: theme.textPrimary }]}>
                      {settings.stripeMode === 'live' ? '● Live (production)' : '● Sandbox (test)'}
                    </Text>
                    <Text style={[styles.settingDescription, { color: settings.stripeMode === 'live' ? '#EF4444' : '#10B981' }]}>
                      {settings.stripeMode === 'live' ? 'Real money — be careful!' : 'Safe for testing'}
                    </Text>
                  </View>
                  <Switch
                    value={settings.stripeMode === 'live'}
                    onValueChange={async (v) => {
                      const next = v ? 'live' : 'sandbox';
                      setSettings(s => ({ ...s, stripeMode: next }));
                      await updateDoc(doc(db, 'settings', 'app'), { stripeMode: next });
                    }}
                    trackColor={{ false: '#10B98160', true: '#EF444460' }}
                    thumbColor={settings.stripeMode === 'live' ? '#EF4444' : '#10B981'}
                  />
                </View>
              </View>

              {/* LTD plan editor */}
              <PlanEditor
                label="Lifetime Plan (LTD)"
                accentColor={theme.primary}
                plan={planDraft.LTD}
                onChange={v => setPlanDraft(d => ({ ...d, LTD: { ...d.LTD, ...v } }))}
                theme={theme}
              />

              {/* Monthly plan editor */}
              <PlanEditor
                label="Pro Monthly Plan"
                accentColor="#8B5CF6"
                plan={planDraft.MONTHLY}
                onChange={v => setPlanDraft(d => ({ ...d, MONTHLY: { ...d.MONTHLY, ...v } }))}
                theme={theme}
              />

              {/* Save */}
              <View style={{ paddingHorizontal: 24, paddingBottom: 48 }}>
                {planSaveMsg ? (
                  <Text style={[styles.saveMsg, { color: planSaveMsg.startsWith('✓') ? '#22C55E' : theme.error }]}>{planSaveMsg}</Text>
                ) : null}
                <Pressable
                  style={[styles.saveBtn, { backgroundColor: theme.primary, opacity: planSaving ? 0.6 : 1 }]}
                  onPress={async () => {
                    setPlanSaving(true);
                    setPlanSaveMsg('');
                    try {
                      await setDoc(doc(db, 'settings', 'plans'), planDraft, { merge: true });
                      setPlanSaveMsg('✓ Plans saved!');
                      setTimeout(() => setPlanSaveMsg(''), 3000);
                    } catch (e) {
                      setPlanSaveMsg('Error: ' + e.message);
                    } finally {
                      setPlanSaving(false);
                    }
                  }}
                  disabled={planSaving}
                >
                  <Text style={styles.saveBtnText}>{planSaving ? 'Saving...' : 'Save plans'}</Text>
                </Pressable>
              </View>
            </>
          )}
        </ScrollView>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1 },
  centered: { flex: 1, justifyContent: 'center', alignItems: 'center', padding: 40 },
  loadingText: { marginTop: 16, fontSize: 16 },
  scrollView: { flex: 1 },
  scrollContent: { paddingBottom: 40 },

  // Header
  header: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingHorizontal: 24, paddingVertical: 16, borderBottomWidth: 1,
  },
  backButton: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  backArrow: { fontSize: 18 },
  backText: { fontSize: 15, fontWeight: '500' },
  headerTitle: { fontSize: 18, fontWeight: '700' },
  adminBadge: { paddingHorizontal: 12, paddingVertical: 5, borderRadius: 999 },
  adminBadgeText: { color: '#fff', fontSize: 12, fontWeight: '700' },

  // Tabs
  tabs: { flexDirection: 'row', borderBottomWidth: 1 },
  tab: { paddingHorizontal: 24, paddingVertical: 14, borderBottomWidth: 2, borderBottomColor: 'transparent' },
  tabText: { fontSize: 15, fontWeight: '600' },

  // Stats
  statsGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 16, padding: 24 },
  statCard: { flex: 1, minWidth: 130, borderRadius: 20, padding: 20, alignItems: 'center', ...Platform.select({ web: { boxShadow: '0 2px 12px rgba(0,0,0,0.04)' } }) },
  statValue: { fontSize: 32, fontWeight: '700', marginBottom: 4 },
  statLabel: { fontSize: 13 },

  // Sections
  section: { marginHorizontal: 24, marginBottom: 16, borderRadius: 24, padding: 24, ...Platform.select({ web: { boxShadow: '0 2px 12px rgba(0,0,0,0.04)' } }) },
  sectionTitle: { fontSize: 16, fontWeight: '700', marginBottom: 8 },
  sectionDescription: { fontSize: 14, lineHeight: 20, marginBottom: 20 },

  // Pricing toggle
  toggleContainer: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingVertical: 16 },
  toggleOption: { flex: 1 },
  toggleLabel: { fontSize: 15, fontWeight: '600', marginBottom: 3 },
  togglePrice: { fontSize: 12 },
  currentModeIndicator: { paddingVertical: 12, paddingHorizontal: 20, borderRadius: 999, alignItems: 'center', marginTop: 8 },
  currentModeText: { color: '#fff', fontSize: 14, fontWeight: '600' },

  // Setting row
  settingRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingVertical: 8 },

  // Plan editor inputs
  inputLabel: { fontSize: 13, fontWeight: '500', marginTop: 12, marginBottom: 4 },
  input: { borderWidth: 1, borderRadius: 10, paddingHorizontal: 14, paddingVertical: 10, fontSize: 14 },
  inputMultiline: { minHeight: 100, textAlignVertical: 'top', paddingTop: 10 },

  // Save button
  saveBtn: { borderRadius: 16, paddingVertical: 18, alignItems: 'center', marginTop: 8 },
  saveBtnText: { color: '#fff', fontSize: 16, fontWeight: '700' },
  saveMsg: { textAlign: 'center', fontSize: 14, fontWeight: '600', marginBottom: 12 },
  settingLabel: { fontSize: 15, fontWeight: '600', marginBottom: 2 },
  settingDescription: { fontSize: 13, lineHeight: 18 },

  // Quick actions
  actionButton: { paddingHorizontal: 18, paddingVertical: 12, borderRadius: 12 },
  actionLabel: { fontSize: 13, fontWeight: '600' },

  // Info section
  infoSection: { marginHorizontal: 24, marginBottom: 24, borderRadius: 24, padding: 24 },
  infoText: { fontSize: 13, lineHeight: 20, marginBottom: 6 },

  // Users tab
  userSearchRow: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 20, paddingVertical: 12, gap: 12, borderBottomWidth: 1 },
  userSearchInput: { flex: 1, borderRadius: 999, borderWidth: 1, paddingHorizontal: 16, paddingVertical: 10, fontSize: 14, ...Platform.select({ web: { outlineStyle: 'none' } }) },
  userCount: { fontSize: 13, fontWeight: '500' },

  userRow: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 20, paddingVertical: 16, borderBottomWidth: 1, gap: 12 },
  userAvatar: { width: 44, height: 44, borderRadius: 22, justifyContent: 'center', alignItems: 'center', flexShrink: 0 },
  userAvatarText: { color: '#fff', fontSize: 18, fontWeight: '700' },
  userName: { fontSize: 15, fontWeight: '600' },
  userEmail: { fontSize: 13, marginTop: 2 },
  userMeta: { fontSize: 12, marginTop: 2 },
  userActions: { flexDirection: 'row', gap: 8, alignItems: 'center', flexShrink: 0 },
  userActionBtn: { paddingHorizontal: 12, paddingVertical: 7, borderRadius: 999, borderWidth: 1.5 },
  userActionText: { fontSize: 12, fontWeight: '600' },

  planBadge: { paddingHorizontal: 8, paddingVertical: 3, borderRadius: 999 },
  planBadgeText: { fontSize: 10, fontWeight: '700' },

  emptyText: { fontSize: 16, textAlign: 'center' },
});

export default AdminScreen;
